import React, { useState, useEffect } from 'react';

// One tab per recipient, each showing exactly the HTML email that person
// would receive, rendered in a sandboxed iframe (srcDoc) rather than
// dangerouslySetInnerHTML — real image loading, no admin-page CSS bleeding
// into the email markup or vice versa, closer to what the recipient's
// inbox will actually show. sandbox="" blocks scripts/forms/popups (this
// is AI-assisted/admin-authored HTML, not audited email markup) while
// still allowing normal image/CSS rendering.
//
// In 'separate' groupMode, each tab also carries its own candidate picker
// — every community member (not limited to who's being emailed) — so the
// admin can hand-pick who gets suggested to THIS specific recipient,
// independently per tab.
const SuggestionPreviewTabs = ({
  previewPlan, groupMode, members,
  perRecipientPartners, setPerRecipientPartners,
}) => {
  const [activeTabUserId, setActiveTabUserId] = useState('');

  const recipients = previewPlan?.recipients || [];

  useEffect(() => {
    if (recipients.length === 0) {
      setActiveTabUserId('');
      return;
    }
    if (!recipients.some((r) => r.userId === activeTabUserId)) {
      setActiveTabUserId(recipients[0].userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipients.map((r) => r.userId).join(',')]);

  if (recipients.length === 0) return null;

  const activeRecipient = recipients.find((r) => r.userId === activeTabUserId) || recipients[0];

  const togglePartner = (partnerUserId) => {
    setPerRecipientPartners((prev) => {
      const current = prev[activeTabUserId] || [];
      const next = current.includes(partnerUserId)
        ? current.filter((id) => id !== partnerUserId)
        : [...current, partnerUserId];
      return { ...prev, [activeTabUserId]: next };
    });
  };

  const activePartnerIds = new Set(perRecipientPartners[activeTabUserId] || []);

  return (
    <div>
      <div className="as-suggestion-recipient-tabs">
        {recipients.map((r) => (
          <button
            key={r.userId}
            type="button"
            className={`as-filter-tab ${activeTabUserId === r.userId ? 'as-filter-tab--active' : ''}`}
            onClick={() => setActiveTabUserId(r.userId)}
          >
            {r.firstName || r.userId}
          </button>
        ))}
      </div>

      <div className="as-suggestion-recipient-card">
        <div className="as-card-meta"><strong>To:</strong> {activeRecipient.firstName} ({activeRecipient.email})</div>
        <div className="as-card-meta"><strong>Subject:</strong> {activeRecipient.subject}</div>
        <iframe
          key={activeRecipient.userId}
          className="as-suggestion-preview-iframe"
          title={`Email preview for ${activeRecipient.firstName || activeRecipient.userId}`}
          srcDoc={activeRecipient.htmlBody}
          sandbox=""
        />

        {groupMode === 'separate' && (
          <div className="as-suggestion-partner-picker">
            <span className="as-card-meta">Who gets suggested to {activeRecipient.firstName || 'this person'}:</span>
            <div className="as-suggestion-partner-picker-list">
              {(members || [])
                .filter((m) => m.userId !== activeTabUserId)
                .map((m) => (
                  <label key={m.userId} className="as-suggestion-checkbox-label">
                    <input
                      type="checkbox"
                      checked={activePartnerIds.has(m.userId)}
                      onChange={() => togglePartner(m.userId)}
                    />
                    {m.firstName || m.userId}
                  </label>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuggestionPreviewTabs;
